import { runAskUserQuestion } from '../../orchestrator/flows/askUserQuestion';
import { handleBotResponse } from '../../orchestrator/flows/botResponse';

/**
 * @typedef {Object} JourneyContentDescriptor
 * @property {() => Promise<object>} load  a dynamic-import loader for the journey's body
 *   component. It is a loader (NOT the component) so the manifest never statically pulls
 *   the heavy content into the warm-while-closed provider chunk — the Shell loads it lazily
 *   on open (see shell/JourneyContent). The Shell owns the loading placeholder.
 * @property {string} exportName  the named export to pull off the resolved module
 * @property {string} [label]     human label for the lazy boundary
 *
 * @typedef {Object} Journey
 * @property {string} id
 * @property {Object<string, (message: object) => import('react').ReactNode[]>} [extraMessageRenderers]
 *   the message types this journey ADDS over the base registry map (a colliding key
 *   overrides the base renderer). Additions only — a journey never re-states the base
 *   map, because doing so would statically import the renderers into the warm path
 *   (see the renderer-free note below); `renderMessageContent` composes the two at the
 *   render site. Omit it when the journey adds no net-new types.
 * @property {Object} flows       the conversational flows this journey runs
 * @property {Object} config      journey-specific config/flags, including which optional
 *   domain BLOCKS the journey opts into (today: `liveAgent`). The orchestrator reads this
 *   the same way it reads `flows` — injected from above by AssistantDataProvider, since it
 *   is mounted ABOVE JourneyProvider and cannot call useJourney().
 * @property {JourneyContentDescriptor} content  where the journey's body component loads from
 * @property {React.ComponentType<{onClose: Function, isMobile?: boolean}>} [header]  a
 *   journey's OWN header component, for a journey whose content does not expect the shared
 *   orchestrator-coupled default markup (today: `legacy`, see AssistantShell.jsx, which
 *   reads this field and renders the default itself when it is absent). Omit it to use the
 *   default.
 *
 *   A DIRECT reference, deliberately unlike `content`: a header is small (no heavier than
 *   what AssistantShell already statically imports for the default one), so there is no
 *   payload worth deferring behind a second lazy boundary — see journeys/legacy/journey.js.
 *   A future header substantial enough to change that calculus should become a
 *   JourneyContentDescriptor instead, same as content.
 * @property {boolean} [bareContent]  true if this journey's content brings its own composer
 *   and message area and must NOT be nested inside the shared DrawerContent frame (today:
 *   `legacy`, whose package renders its own composer — nesting it would produce two). See
 *   AssistantShell.jsx, which reads this field.
 */

/**
 * The default Magic Apron Assistant journey manifest — a lazy loader like every journey's.
 * MUST STAY RENDERER-FREE (Seq E.1): `legacy`/`paintColorPicker` import it statically.
 * See .claude/context/journey-resolution.md.
 *
 * @type {Journey}
 */
export const AssistantJourney = {
  id: 'assistant',
  flows: { runAskUserQuestion, handleBotResponse },
  // OPT-IN (`=== true`): omitting a block here means the journey runs without it.
  config: { liveAgent: true, queryLayer: true },
  content: {
    load: () => import('./MagicApronAssistant'),
    exportName: 'MagicApronAssistant',
    label: 'Assistant',
  },
};

// Default export required by useResolvedJourney's mod.default read — see
// .claude/context/journey-resolution.md for what breaks silently without it.
export default AssistantJourney;
